import { z } from "zod";
import type { MeetSlot } from "./meets";
import { rankByValue } from "./ranking";
import { RESULT_FAMILY_ARRAY_MAX, RESULT_RANK_MAX, START_LIST_SLOTS_MAX } from "./results";
import type { IrmCode } from "./vocab";

/**
 * Timing feed — what a timing system (touchpads, start blocks, photo-finish)
 * says about one race, in ONE vendor-neutral shape, and the pure step that
 * turns it into the rows `PATCH /meets/:id/results` takes.
 *
 *   vendor export ──adapter──▶ TimingPayload ──normalizeTiming()──▶ ResultRowBody[]
 *
 * A vendor adapter only converts units and names into `timingPayload`; every
 * rule lives here, once, so all vendors are held to the same ones:
 *
 * - **The feed fills, it never decides.** It sets reaction, splits, finish
 *   and DNS / DNF / DSQ. It never CLEARS an IRM nor changes one already on
 *   the slot (a DSQ is the referee's), and it never writes a slot in
 *   `protect` (one an operator typed — the manual override).
 * - **Unchanged values write nothing.** Systems resend the whole race on
 *   every touch; every save is an audit row, so a resent lane is no row.
 * - **Ranks are ours.** Computed with `rankByValue` over every slot after
 *   the feed's values; slots whose rank moved get a rank-only row — the same
 *   commit OVR's row engine makes. The device's `place` is only compared.
 * - **A doubtful lane is skipped whole and reported**, never half-applied:
 *   the operator sees it in the grid and keys it in.
 * - **A payload for another race is refused whole** (`RACE_MISMATCH`): a
 *   timing PC left on the previous heat must not write this one.
 *
 * Times are integer milliseconds on the wire. `resolution_ms` 10 truncates
 * finish and splits to hundredths (World Aquatics SW 11.3: swimming times are
 * recorded to 1/100 s, and thousandths never break a tie); reaction times
 * keep thousandths.
 */

export const TIMING_PAYLOAD_SCHEMA = "seagames.timing/v1";

/** What a timing system says about a lane's swimmer / runner. Absent = no opinion. */
export const TIMING_LANE_STATUSES = ["OK", "DNS", "DNF", "DSQ"] as const;
export type TimingLaneStatus = (typeof TIMING_LANE_STATUSES)[number];

/** A lane identifier: what the start list writes in `lane` ("4"), or a bib for races without lanes. */
export const TIMING_LANE_PATTERN = /^[A-Za-z0-9-]{1,12}$/;

/** 24 hours: past any timed race on the programme (a road race, open water). */
export const TIMING_TIME_MAX_MS = 24 * 3_600_000;
/** A reaction time above 10 s is a pad or cable fault, not a start. */
export const TIMING_REACTION_MAX_MS = 10_000;

const ms = (max: number = TIMING_TIME_MAX_MS) => z.number().int().min(0).max(max);
const laneCode = () => z.union([z.number().int().min(0).max(999), z.string().trim().regex(TIMING_LANE_PATTERN)]);

const timingLane = z
	.object({
		/** The lane, as the start list has it. Exactly one of `lane` / `bib`. */
		lane: laneCode().optional(),
		/** The bib, for races without lanes (open water, road). */
		bib: laneCode().optional(),
		reaction_ms: ms(TIMING_REACTION_MAX_MS).optional(),
		/** Elapsed time at each distance — cumulative, as pads report them. */
		splits: z
			.array(z.object({ distance_m: z.number().positive().max(100_000), cumulative_ms: ms() }).strict())
			.max(RESULT_FAMILY_ARRAY_MAX)
			.optional(),
		finish_ms: ms().optional(),
		status: z.enum(TIMING_LANE_STATUSES).optional(),
		/** The device's own place — compared with ours, never stored. */
		place: z.number().int().min(1).max(RESULT_RANK_MAX).optional(),
	})
	.strict()
	.refine((l) => (l.lane === undefined) !== (l.bib === undefined), { message: "Send lane or bib, not both", path: ["lane"] });

/**
 * One message from a timing system about one race. Systems send a message per
 * touch (a 50 m split, a finish) or the whole race again; both are fine.
 * `sequence` rises per race on the device: the endpoint keeps the last one
 * and drops older or repeated messages — the normalizer is stateless.
 */
export const timingPayload = z
	.object({
		schema: z.literal(TIMING_PAYLOAD_SCHEMA),
		source: z
			.object({
				/** The system, upper-case: `OMEGA_QUANTUM`, `SWISS_TIMING`, `DAKTRONICS`, `MANUAL_BRIDGE`. */
				system: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
				device_id: z.string().trim().min(1).max(64).optional(),
			})
			.strict(),
		race: z
			.object({
				/** The meet the device believes it is timing — refused when it is not this one. */
				meet_id: z.string().uuid().optional(),
				distance_m: z.number().positive().max(100_000).optional(),
			})
			.strict(),
		sequence: z.number().int().min(0),
		/** When the device sent it, with an offset. */
		sent_at: z.string().datetime({ offset: true }),
		lanes: z.array(timingLane).min(1).max(START_LIST_SLOTS_MAX),
	})
	.strict();
export type TimingPayload = z.infer<typeof timingPayload>;
export type TimingLane = TimingPayload["lanes"][number];

// ─── Normalizing ─────────────────────────────────────────────────────────────

/**
 * Why a lane (or the whole payload) was not applied as sent.
 *
 *   RACE_MISMATCH          payload for another meet / distance — nothing applied
 *   UNKNOWN_LANE           no slot of the meet has this lane / bib
 *   DUPLICATE_LANE         the payload lists the lane twice — both skipped
 *   AMBIGUOUS_LANE         two slots share the lane / bib
 *   EMPTY_SLOT             the slot waits for its competitor
 *   MANUAL_OVERRIDE        an operator typed this slot's result — the feed keeps off
 *   SPLITS_UNKEYED         the slot's stored splits have no distances to merge into
 *   SPLIT_BEYOND_DISTANCE  a split past the race distance
 *   SPLITS_OUT_OF_ORDER    elapsed times that do not rise with distance
 *   FINISH_SPLIT_MISMATCH  a split at the race distance that is not the finish
 *   FINISH_BEFORE_SPLIT    a finish not later than the last split
 *   STATUS_WITH_FINISH     DNS with a finish time
 *   — applied, but worth a look —
 *   IRM_DIFFERS            the device's DNS / DNF / DSQ differs from the slot's IRM (the slot's is kept)
 *   PLACE_MISMATCH         the device's place differs from the rank we computed
 */
export const TIMING_PROBLEM_CODES = [
	"RACE_MISMATCH",
	"UNKNOWN_LANE",
	"DUPLICATE_LANE",
	"AMBIGUOUS_LANE",
	"EMPTY_SLOT",
	"MANUAL_OVERRIDE",
	"SPLITS_UNKEYED",
	"SPLIT_BEYOND_DISTANCE",
	"SPLITS_OUT_OF_ORDER",
	"FINISH_SPLIT_MISMATCH",
	"FINISH_BEFORE_SPLIT",
	"STATUS_WITH_FINISH",
	"IRM_DIFFERS",
	"PLACE_MISMATCH",
] as const;
export type TimingProblemCode = (typeof TIMING_PROBLEM_CODES)[number];

/** The codes after which the lane was still applied. */
export const TIMING_WARNING_CODES: ReadonlySet<TimingProblemCode> = new Set<TimingProblemCode>(["IRM_DIFFERS", "PLACE_MISMATCH"]);

export interface TimingProblem {
	code: TimingProblemCode;
	/** The lane or bib as the payload sent it; null for a payload-level problem. */
	lane: string | null;
	meet_competitor_id: string | null;
}

/** The slot fields the normalizer reads. */
export type TimingSlot = Pick<MeetSlot, "meet_competitor_id" | "version" | "lane" | "bib" | "irm" | "result_value" | "result_display" | "rank" | "detail"> & {
	/** Null while the slot waits for its competitor. */
	competitor: unknown | null;
};

export interface TimingContext {
	meetId: string;
	slots: readonly TimingSlot[];
	/** The meet's `config.distance_m`, null when unknown. */
	distanceM: number | null;
	lowerIsBetter: boolean;
	/** 10 = hundredths (swimming); 1 = as sent. Applies to finish and splits, never to reaction. */
	resolutionMs: 1 | 10;
	/** Slots an operator keyed by hand: never written by the feed. */
	protect?: ReadonlySet<string>;
}

/** One row for `PATCH /meets/:id/results` — the keys of `resultRowBody` the feed may write. */
export interface TimingRow {
	meet_competitor_id: string;
	version: number;
	irm?: IrmCode;
	result_value?: number;
	result_display?: string | null;
	rank?: number | null;
	detail?: Record<string, unknown>;
}

export interface TimingOutcome {
	/** Changed slots only, in slot order; empty when nothing changed. At most one per slot. */
	rows: TimingRow[];
	problems: TimingProblem[];
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** "04" and 4 are lane "4"; letters upper-cased. */
export function timingLaneKey(raw: string | number | null | undefined): string | null {
	if (raw === null || raw === undefined) return null;
	const s = String(raw).trim().toUpperCase();
	if (s === "") return null;
	return /^\d+$/.test(s) ? String(Number(s)) : s;
}

/**
 * A time as the results board prints it: `49.75`, `1:52.31`, `1:02:03.40`.
 * Hundredths unless the time has thousandths. The same output as OVR's
 * `formatTime(ms, { precision: 2 })` (`apps/ovr/src/lib/entry/time.ts`) —
 * pinned by `time-parity.test.ts` there.
 */
export function formatTimingTime(timeMs: number): string {
	const whole = Math.round(timeMs);
	const decimals = whole % 10 !== 0 ? 3 : 2;
	const totalSeconds = Math.floor(whole / 1000);
	const fraction = String(whole % 1000)
		.padStart(3, "0")
		.slice(0, decimals);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	const pad = (n: number) => String(n).padStart(2, "0");
	const clock = hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : minutes > 0 ? `${minutes}:${pad(seconds)}` : String(seconds);
	return `${clock}.${fraction}`;
}

const truncate = (value: number, resolution: 1 | 10) => value - (value % resolution);

interface StoredSplit {
	seq: number;
	distance_m?: number;
	time_ms: number;
	cumulative_ms?: number;
}

/** The slot's stored splits as distance → elapsed, or null when any lacks a distance (manual laps). */
function storedCumulative(detail: Record<string, unknown>): Map<number, number> | null {
	const raw = Array.isArray(detail.splits) ? (detail.splits as StoredSplit[]) : [];
	const out = new Map<number, number>();
	let running = 0;
	for (const s of [...raw].sort((a, b) => a.seq - b.seq)) {
		if (!isNum(s.distance_m)) return null;
		running = isNum(s.cumulative_ms) ? s.cumulative_ms : running + s.time_ms;
		out.set(s.distance_m, running);
	}
	return out;
}

/** distance → elapsed as the stored `splits` array: seq from 1, each split's own time and the elapsed. */
function splitsArray(cumul: ReadonlyMap<number, number>): StoredSplit[] {
	let previous = 0;
	return [...cumul.entries()]
		.sort((a, b) => a[0] - b[0])
		.map(([distance, elapsed], i) => {
			const split = { seq: i + 1, distance_m: distance, time_ms: elapsed - previous, cumulative_ms: elapsed };
			previous = elapsed;
			return split;
		});
}

/** Stored splits equal built ones, whatever order their keys were written in. */
function sameSplits(stored: unknown, built: readonly StoredSplit[]): boolean {
	const canon = (s: StoredSplit) => [s.seq, s.distance_m ?? null, s.time_ms, s.cumulative_ms ?? null];
	const raw = Array.isArray(stored) ? (stored as StoredSplit[]) : [];
	return JSON.stringify(raw.map(canon)) === JSON.stringify(built.map(canon));
}

type LaneWrite = { slot: TimingSlot; irm: IrmCode | null; value: number | null; display: string | null; detail: Record<string, unknown>; changed: boolean };

/** One lane against its slot: the values after it, or the reason it is skipped. */
function applyLane(lane: TimingLane, slot: TimingSlot, ctx: TimingContext, warn: (code: TimingProblemCode) => void): LaneWrite | TimingProblemCode {
	if (slot.competitor === null) return "EMPTY_SLOT";
	if (ctx.protect?.has(slot.meet_competitor_id)) return "MANUAL_OVERRIDE";

	let finish = lane.finish_ms === undefined ? null : truncate(lane.finish_ms, ctx.resolutionMs);
	if (lane.status === "DNS" && finish !== null) return "STATUS_WITH_FINISH";

	// The touch at the wall IS the finish: kept as the result, not as a split.
	const intermediate: { distance_m: number; elapsed: number }[] = [];
	for (const s of lane.splits ?? []) {
		const elapsed = truncate(s.cumulative_ms, ctx.resolutionMs);
		if (ctx.distanceM !== null && s.distance_m > ctx.distanceM) return "SPLIT_BEYOND_DISTANCE";
		if (ctx.distanceM !== null && s.distance_m === ctx.distanceM) {
			if (finish !== null && finish !== elapsed) return "FINISH_SPLIT_MISMATCH";
			finish = elapsed;
		} else intermediate.push({ distance_m: s.distance_m, elapsed });
	}

	// Splits: merged by distance into what the slot holds, the feed winning at its own distances.
	let cumul: Map<number, number> | null = null;
	if (intermediate.length > 0) {
		cumul = storedCumulative(slot.detail ?? {});
		if (cumul === null) return "SPLITS_UNKEYED";
		for (const s of intermediate) cumul.set(s.distance_m, s.elapsed);
		const ordered = [...cumul.entries()].sort((a, b) => a[0] - b[0]);
		if (ordered.some(([, e], i) => i > 0 && e <= (ordered[i - 1] as [number, number])[1])) return "SPLITS_OUT_OF_ORDER";
	}
	const value = finish ?? slot.result_value;
	if (finish !== null || cumul !== null) {
		const elapsed = cumul ?? storedCumulative(slot.detail ?? {});
		const lastSplit = elapsed && elapsed.size > 0 ? Math.max(...elapsed.values()) : null;
		if (isNum(value) && lastSplit !== null && value <= lastSplit) return "FINISH_BEFORE_SPLIT";
	}

	// IRM: the feed may set one on a clean slot; it never clears or replaces one.
	let irm = slot.irm;
	const said = lane.status === "DNS" || lane.status === "DNF" || lane.status === "DSQ" ? lane.status : null;
	if (said !== null && irm === null) irm = said;
	else if (said !== null && irm !== said) warn("IRM_DIFFERS");

	const detail: Record<string, unknown> = { ...(slot.detail ?? {}) };
	if (lane.reaction_ms !== undefined) detail.reaction_ms = lane.reaction_ms;
	if (cumul !== null) {
		const built = splitsArray(cumul);
		if (!sameSplits(detail.splits, built)) detail.splits = built;
	}
	// The board text is rewritten only when what it shows changed: a meet shown to
	// thousandths ("49.750") keeps its own text while the device resends 49.75.
	const display = irm !== slot.irm || value !== slot.result_value ? (irm ?? (isNum(value) ? formatTimingTime(value) : null)) : slot.result_display;
	const changed =
		irm !== slot.irm || value !== slot.result_value || display !== slot.result_display || JSON.stringify(detail) !== JSON.stringify(slot.detail ?? {});
	return { slot, irm, value: isNum(value) ? value : null, display, detail, changed };
}

/**
 * A timing payload against the meet's slots: the result rows to save (only
 * what changed, ranks included) and every problem met. Pure — the endpoint
 * validates the payload with `timingPayload`, loads the slots, calls this,
 * and saves `rows` in one statement.
 */
export function normalizeTiming(payload: TimingPayload, ctx: TimingContext): TimingOutcome {
	const problems: TimingProblem[] = [];
	const report = (code: TimingProblemCode, lane: string | null, slotId: string | null = null) => problems.push({ code, lane, meet_competitor_id: slotId });

	const raceDistance = payload.race.distance_m;
	if ((payload.race.meet_id !== undefined && payload.race.meet_id !== ctx.meetId) || (raceDistance !== undefined && ctx.distanceM !== null && raceDistance !== ctx.distanceM)) {
		report("RACE_MISMATCH", null);
		return { rows: [], problems };
	}

	// lane / bib → slot, refusing keys two slots share.
	const byKey = new Map<string, TimingSlot | "ambiguous">();
	for (const slot of ctx.slots) {
		for (const key of [timingLaneKey(slot.lane) && `L:${timingLaneKey(slot.lane)}`, timingLaneKey(slot.bib) && `B:${timingLaneKey(slot.bib)}`]) {
			if (!key) continue;
			byKey.set(key, byKey.has(key) ? "ambiguous" : slot);
		}
	}
	const keyOf = (l: TimingLane) => (l.lane !== undefined ? `L:${timingLaneKey(l.lane)}` : `B:${timingLaneKey(l.bib)}`);
	const shown = (l: TimingLane) => String(l.lane ?? l.bib);
	const counts = new Map<string, number>();
	for (const l of payload.lanes) counts.set(keyOf(l), (counts.get(keyOf(l)) ?? 0) + 1);

	const writes = new Map<string, LaneWrite>();
	const places = new Map<string, { place: number; lane: string }>();
	for (const lane of payload.lanes) {
		const key = keyOf(lane);
		if ((counts.get(key) ?? 0) > 1) {
			report("DUPLICATE_LANE", shown(lane));
			continue;
		}
		const slot = byKey.get(key);
		if (slot === undefined) {
			report("UNKNOWN_LANE", shown(lane));
			continue;
		}
		if (slot === "ambiguous") {
			report("AMBIGUOUS_LANE", shown(lane));
			continue;
		}
		const warnings: TimingProblemCode[] = [];
		const write = applyLane(lane, slot, ctx, (c) => warnings.push(c));
		if (typeof write === "string") {
			report(write, shown(lane), slot.meet_competitor_id);
			continue;
		}
		for (const w of warnings) report(w, shown(lane), slot.meet_competitor_id);
		writes.set(slot.meet_competitor_id, write);
		if (lane.place !== undefined) places.set(slot.meet_competitor_id, { place: lane.place, lane: shown(lane) });
	}

	// Ranks over every filled slot, with the feed's values in place.
	const filled = ctx.slots.filter((s) => s.competitor !== null);
	const ranks = rankByValue(
		filled.map((s) => {
			const w = writes.get(s.meet_competitor_id);
			return { id: s.meet_competitor_id, value: w ? w.value : s.result_value, irm: w ? w.irm : s.irm };
		}),
		ctx.lowerIsBetter,
	);
	for (const [slotId, { place, lane }] of places) if (ranks.get(slotId) !== place) report("PLACE_MISMATCH", lane, slotId);

	const rows: TimingRow[] = [];
	for (const slot of filled) {
		const w = writes.get(slot.meet_competitor_id);
		const rank = ranks.get(slot.meet_competitor_id) ?? null;
		const row: TimingRow = { meet_competitor_id: slot.meet_competitor_id, version: slot.version };
		if (w?.changed) {
			if (w.irm !== slot.irm && w.irm !== null) row.irm = w.irm;
			if (w.value !== slot.result_value && w.value !== null) row.result_value = w.value;
			if (w.display !== slot.result_display) row.result_display = w.display;
			if (JSON.stringify(w.detail) !== JSON.stringify(slot.detail ?? {})) row.detail = w.detail;
		}
		if (rank !== slot.rank) row.rank = rank;
		if (Object.keys(row).length > 2) rows.push(row);
	}
	return { rows, problems };
}

// ─── POST /meets/:id/timing ──────────────────────────────────────────────────

/**
 * `POST /meets/:id/timing` — one timing message for a TIMED meet, by a login
 * that may write its results (`competition.results` in its sport). Refused
 * (nothing written): not TIMED (422 VALIDATION_FAILED `{ family }`), the meet
 * past LIVE (409 CONFLICT `{ result_status }`: UNOFFICIAL and OFFICIAL
 * results are the officials', not the pads'), a locked event, no access.
 *
 * 200 with `accepted: false` when the device already sent a later message
 * (by `sent_at`, then `sequence`): a repeat or a late packet is no error for
 * the device, and nothing is written. Otherwise the changed rows are saved in
 * one statement (`saved`, possibly empty) and every lane problem is listed.
 */
export interface TimingIngestResponse {
	meet_id: string;
	/** False = an older or repeated message, dropped. */
	accepted: boolean;
	/** The slots written, with their new versions. */
	saved: { meet_competitor_id: string; version: number }[];
	problems: TimingProblem[];
}

// ─── What operators see: the problems kept per device ────────────────────────

/** The lanes a message named, as keys (`timingLaneKey`): lane or bib, as sent. */
const sentKeys = (lanes: readonly TimingLane[]) => new Set(lanes.map((l) => timingLaneKey(l.lane ?? l.bib)).filter((k): k is string => k !== null));

/**
 * One device's problems after a message, from what it had before
 * (`meet_timing_feeds.last_problems`, 0030):
 *
 * - a lane the message names takes the message's verdict — its problems, or
 *   none (a clean resend clears a lane);
 * - a lane it does not name keeps its earlier verdict, so a lane skipped at
 *   the 50 m touch stays flagged while the finish arrives for the others;
 * - the race-level verdict (`lane: null`) is the message's own;
 * - a message refused whole (`RACE_MISMATCH`) judged no lane, so every lane
 *   keeps its earlier verdict.
 *
 * Repeats are dropped; race-level first, then by lane (numeric where it can).
 */
export function mergeTimingProblems(previous: readonly TimingProblem[], lanes: readonly TimingLane[], latest: readonly TimingProblem[]): TimingProblem[] {
	const refusedWhole = latest.some((p) => p.code === "RACE_MISMATCH");
	const named = refusedWhole ? new Set<string>() : sentKeys(lanes);
	const kept = previous.filter((p) => p.lane !== null && !named.has(timingLaneKey(p.lane) ?? ""));
	const out: TimingProblem[] = [];
	const seen = new Set<string>();
	for (const p of [...latest.filter((x) => x.lane === null), ...kept, ...latest.filter((x) => x.lane !== null)]) {
		const key = `${p.code}|${timingLaneKey(p.lane) ?? ""}|${p.meet_competitor_id ?? ""}`;
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(p);
	}
	const rank = (lane: string | null) => {
		if (lane === null) return [-1, ""] as const;
		const k = timingLaneKey(lane) ?? "";
		return /^\d+$/.test(k) ? ([Number(k), k] as const) : ([Number.MAX_SAFE_INTEGER, k] as const);
	};
	return out.sort((a, b) => {
		const [an, ak] = rank(a.lane);
		const [bn, bk] = rank(b.lane);
		return an - bn || ak.localeCompare(bk);
	});
}

/**
 * A timing device that has written this meet, on `GET /meets/:id`
 * (`MeetDetail.timing_feeds`, by system then device): when it last spoke and
 * what it left unapplied. Empty until a device sends a message; emptied when
 * the meet's results are cleared.
 */
export interface MeetTimingFeed {
	system: string;
	/** '' when the device sent no id. */
	device_id: string;
	last_sent_at: string;
	received_at: string;
	problems: TimingProblem[];
}
